/**
 * Timer 页面入口文件
 * 整合所有模块,初始化 Timer 功能
 */

// 导入模块
import CountdownTimer from '../timer/countdown.js';
import Stopwatch from '../timer/stopwatch.js';
import TimerDisplay from '../timer/timer-display.js';
import TimerControls from '../timer/timer-controls.js';
import TimerAlarm from '../timer/timer-alarm.js';
import TimerSettingsManager from '../settings/timer-settings-manager.js';

// 创建 inline Web Worker，用于后台精确到期检测（不受页面可见性节流）
// 支持多个命名定时器：complete（最终到期）、bell-0/bell-1/...（间隔提示音）
function createTimerWorker() {
    const workerCode = `
        const timers = {};
        self.onmessage = function(e) {
            const d = e.data;
            if (d.type === 'start') {
                // 启动单个命名定时器
                const id = d.id || 'complete';
                if (timers[id]) clearTimeout(timers[id]);
                timers[id] = setTimeout(() => {
                    self.postMessage({ type: 'timer', id: id });
                    delete timers[id];
                }, Math.max(0, d.delay));
            } else if (d.type === 'cancel') {
                // 取消指定定时器，不传 id 则取消全部
                if (d.id) {
                    if (timers[d.id]) {
                        clearTimeout(timers[d.id]);
                        delete timers[d.id];
                    }
                } else {
                    for (const key in timers) {
                        clearTimeout(timers[key]);
                    }
                    for (const key in timers) {
                        delete timers[key];
                    }
                }
            }
        };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    return new Worker(URL.createObjectURL(blob));
}

// 初始化应用
document.addEventListener('DOMContentLoaded', () => {
    // 创建实例
    const countdown = new CountdownTimer();
    const stopwatch = new Stopwatch();
    const display = new TimerDisplay();
    const controls = new TimerControls();
    const alarm = new TimerAlarm();
    const settings = new TimerSettingsManager();

    // 后台到期检测 Worker（不受页面可见性节流）
    let timerWorker = null;
    try {
        timerWorker = createTimerWorker();
        timerWorker.onmessage = (e) => {
            if (e.data.type !== 'timer' || currentMode !== 'countdown') {
                return;
            }
            if (e.data.id === 'complete') {
                // 倒计时到期
                handleCountdownComplete();
            } else if (e.data.id && e.data.id.startsWith('bell-')) {
                // Meditation 间隔提示音，同步推进 nextBellSeconds 防止 rAF 恢复后重复触发
                if (meditationState.nextBellSeconds !== null) {
                    meditationState.nextBellSeconds -= meditationState.intervalSeconds;
                    if (meditationState.nextBellSeconds <= 0) {
                        meditationState.nextBellSeconds = null;
                    }
                }
                alarm.playShortBeep();
            }
        };
    } catch (e) {
        // Worker 创建失败时静默降级，仍使用 rAF 机制
    }

    let currentMode = settings.getCurrentMode(); // 'countdown' 或 'stopwatch'
    let currentTimer = countdown; // 当前活动的计时器
    let currentCountdownType = settings.getCountdownType(); // 'normal' | 'interval' | 'meditation'
    let intervalState = buildIntervalState(settings.getIntervalSettings());
    let meditationState = buildMeditationState(settings.getMeditationSettings());
    const intervalLabels = display.getIntervalLabels();

    // 初始化
    init();

    function init() {
        // 初始化铃声开关状态
        alarm.setEnabled(settings.getAlarmEnabled());
        controls.setStopAlarmVisible(false);

        // 设置回调
        setupCallbacks();

        if (currentMode === 'stopwatch') {
            initStopwatchView();
        } else {
            initCountdownView();
        }
    }

    /**
     * 设置所有回调函数
     */
    function setupCallbacks() {
        // Countdown 回调
        countdown.on('onTick', (timeStr) => {
            if (currentMode !== 'countdown') {
                return;
            }

            const state = countdown.getState();
            if (currentCountdownType === 'normal') {
                display.updateTime(timeStr, state.isNegative);
                return;
            }

            if (!state.isNegative) {
                display.updateTime(timeStr, false);
            }

            if (currentCountdownType === 'meditation') {
                handleMeditationTick(state.remainingSeconds);
            }
        });

        countdown.on('onComplete', () => {
            if (currentMode !== 'countdown') {
                return;
            }
            handleCountdownComplete();
        });

        // Stopwatch 回调
        stopwatch.on('onTick', (timeData) => {
            display.updateTime(timeData);
        });

        stopwatch.on('onSplit', (splitData) => {
            display.addSplitRecord(splitData);
        });

        // 控制按钮回调
        controls.on('onStartPause', (action) => {
            handleStartPause(action);
        });

        controls.on('onResetSplit', (action) => {
            handleResetSplit(action);
        });

        controls.on('onStopAlarm', () => {
            alarm.stop();
        });

        alarm.on('onStart', () => {
            controls.setStopAlarmVisible(true);
        });

        alarm.on('onStop', () => {
            controls.setStopAlarmVisible(false);
        });

        // 设置面板回调
        settings.on('onModeChange', (mode) => {
            handleModeChange(mode);
        });

        settings.on('onCountdownTimeChange', (time) => {
            if (currentCountdownType !== 'normal') {
                return;
            }
            alarm.stop();
            controls.setStopAlarmVisible(false);

            if (countdown.isRunning) {
                countdown.setTime(time.hours, time.minutes, time.seconds);
                startWorkerTimer(countdown.remainingMs);
            } else {
                resetCountdownToTime(time);
                updateButtonState('idle');
            }
        });

        settings.on('onCountdownTypeChange', (type) => {
            applyCountdownType(type, true);
        });

        settings.on('onIntervalSettingsChange', () => {
            if (currentCountdownType !== 'interval') {
                intervalState = buildIntervalState(settings.getIntervalSettings());
                return;
            }
            resetIntervalTimer();
        });

        settings.on('onMeditationSettingsChange', () => {
            if (currentCountdownType !== 'meditation') {
                meditationState = buildMeditationState(settings.getMeditationSettings());
                return;
            }
            resetMeditationTimer();
        });

        settings.on('onShowMillisecondsChange', (show) => {
            stopwatch.setShowMilliseconds(show);
            display.updateShowMilliseconds(show);

            // 如果正在运行,需要重新启动以改变更新频率
            if (stopwatch.isRunning) {
                stopwatch._restartInterval();
            }
        });

        settings.on('onAlarmSoundChange', (enabled) => {
            alarm.setEnabled(enabled);
        });
    }

    /**
     * 初始化 Countdown 视图
     */
    function initCountdownView() {
        currentTimer = countdown;
        display.toggleSplitRecords(false);
        applyCountdownType(currentCountdownType, true);
    }

    /**
     * 初始化 Stopwatch 视图
     */
    function initStopwatchView() {
        currentTimer = stopwatch;
        stopwatch.setShowMilliseconds(settings.getShowMilliseconds());
        stopwatch.reset();
        display.updateTime(stopwatch.getFormattedTime());
        display.toggleSplitRecords(false);
        display.hideIntervalStatus();
        controls.updateButtonsForStopwatch('idle');
    }

    /**
     * 应用倒计时子类型
     * @param {string} type - normal | interval | meditation
     * @param {boolean} shouldReset - 是否重置到初始状态
     */
    function applyCountdownType(type, shouldReset = false) {
        currentCountdownType = type;
        alarm.stop();
        controls.setStopAlarmVisible(false);

        if (currentMode !== 'countdown') {
            return;
        }

        if (currentCountdownType === 'interval') {
            intervalState = buildIntervalState(settings.getIntervalSettings());
            displayIntervalStatus();

            if (shouldReset) {
                resetCountdownToTime(intervalState.workTime);
                updateButtonState('idle');
            }
            return;
        }

        if (currentCountdownType === 'meditation') {
            meditationState = buildMeditationState(settings.getMeditationSettings());
            display.hideIntervalStatus();

            if (shouldReset) {
                resetCountdownToTime(meditationState.totalTime);
                updateButtonState('idle');
            }
            return;
        }

        display.hideIntervalStatus();

        if (shouldReset) {
            const countdownTime = settings.getCountdownTime();
            resetCountdownToTime(countdownTime);
            updateButtonState('idle');
        }
    }

    /**
     * 倒计时到期统一处理（rAF 和 Worker 都调用此函数）
     * alarm.start() 内部有防重入保护，不会重复触发
     */
    function handleCountdownComplete() {
        if (currentCountdownType === 'interval') {
            handleIntervalComplete();
            return;
        }

        if (currentCountdownType === 'meditation') {
            handleMeditationComplete();
            return;
        }

        alarm.start();
    }

    /**
     * 通知 Worker 开始后台计时（最终到期）
     */
    function startWorkerTimer(delayMs) {
        if (timerWorker && delayMs > 0) {
            timerWorker.postMessage({ type: 'start', id: 'complete', delay: delayMs });
        }
    }

    /**
     * 取消 Worker 全部定时器
     */
    function cancelWorkerTimer() {
        if (timerWorker) {
            timerWorker.postMessage({ type: 'cancel' });
        }
    }

    /**
     * 调度 Meditation 间隔提示音到 Worker
     * 计算所有提示音的触发时间点，一次性全部注册
     */
    function scheduleMeditationBells(remainingMs) {
        if (!timerWorker) {
            return;
        }
        if (meditationState.intervalSeconds <= 0 || meditationState.nextBellSeconds === null) {
            return;
        }

        const totalRemainingMs = remainingMs;
        let nextBellSec = meditationState.nextBellSeconds;
        let bellIndex = 0;

        // remainingSeconds 从大到小递减，nextBellSeconds 也是从大到小
        // 当 remainingSeconds 降到 nextBellSeconds 时触发提示音
        // 转换为延迟毫秒：delayMs = totalRemainingMs - nextBellSec * 1000
        while (nextBellSec !== null && nextBellSec > 0) {
            const delayMs = totalRemainingMs - nextBellSec * 1000;
            if (delayMs > 0) {
                timerWorker.postMessage({
                    type: 'start',
                    id: 'bell-' + bellIndex,
                    delay: delayMs
                });
                bellIndex++;
            }
            nextBellSec -= meditationState.intervalSeconds;
            if (nextBellSec <= 0) {
                break;
            }
        }
    }

    /**
     * 重置 Countdown 到指定时间
     */
    function resetCountdownToTime(time) {
        countdown.pause();
        countdown.setTime(time.hours, time.minutes, time.seconds);
        display.updateTime(countdown.getFormattedTime());
    }

    /**
     * 重置 Interval 计时
     */
    function resetIntervalTimer() {
        intervalState = buildIntervalState(settings.getIntervalSettings());
        resetCountdownToTime(intervalState.workTime);
        displayIntervalStatus();
        alarm.stop();
        controls.setStopAlarmVisible(false);
        updateButtonState('idle');
    }

    /**
     * 重置 Meditation 计时
     */
    function resetMeditationTimer() {
        meditationState = buildMeditationState(settings.getMeditationSettings());
        resetCountdownToTime(meditationState.totalTime);
        display.hideIntervalStatus();
        alarm.stop();
        controls.setStopAlarmVisible(false);
        updateButtonState('idle');
    }

    /**
     * 构建 Interval 状态
     */
    function buildIntervalState(intervalSettings) {
        const workTime = normalizeTime(intervalSettings.work);
        const restTime = normalizeTime(intervalSettings.rest);
        const totalRounds = Math.max(1, parseInt(intervalSettings.rounds || 1, 10));

        return {
            phase: 'work',
            round: 1,
            totalRounds,
            workTime,
            restTime,
            workSeconds: toSeconds(workTime),
            restSeconds: toSeconds(restTime),
            isComplete: false
        };
    }

    /**
     * 构建 Meditation 状态
     */
    function buildMeditationState(meditationSettings) {
        const totalTime = normalizeTime(meditationSettings.total);
        const intervalTime = normalizeTime(meditationSettings.interval);
        const totalSeconds = toSeconds(totalTime);
        const intervalSeconds = toSeconds(intervalTime);
        const hasInterval = intervalSeconds > 0 && totalSeconds > intervalSeconds;

        return {
            totalTime,
            intervalTime,
            totalSeconds,
            intervalSeconds,
            nextBellSeconds: hasInterval ? totalSeconds - intervalSeconds : null,
            isComplete: false
        };
    }

    /**
     * Interval 状态显示
     */
    function displayIntervalStatus() {
        const phaseLabel = intervalState.phase === 'rest' ? intervalLabels.rest : intervalLabels.work;
        const roundText = `${intervalState.round}/${intervalState.totalRounds}`;
        display.updateIntervalStatus(phaseLabel, roundText);
    }

    /**
     * 处理 Interval 阶段完成
     */
    function handleIntervalComplete() {
        if (intervalState.workSeconds === 0 && intervalState.restSeconds === 0) {
            finishIntervalCycle();
            return;
        }

        alarm.stop();

        if (intervalState.phase === 'work') {
            if (intervalState.restSeconds > 0) {
                intervalState.phase = 'rest';
                displayIntervalStatus();
                resetCountdownToTime(intervalState.restTime);
                countdown.start();
                startWorkerTimer(countdown.remainingMs);
                alarm.start();
                return;
            }

            if (intervalState.round < intervalState.totalRounds) {
                intervalState.round += 1;
                intervalState.phase = 'work';
                displayIntervalStatus();
                resetCountdownToTime(intervalState.workTime);
                countdown.start();
                startWorkerTimer(countdown.remainingMs);
                alarm.start();
                return;
            }

            finishIntervalCycle();
            return;
        }

        if (intervalState.round < intervalState.totalRounds) {
            intervalState.round += 1;
            intervalState.phase = 'work';
            displayIntervalStatus();
            resetCountdownToTime(intervalState.workTime);
            countdown.start();
            startWorkerTimer(countdown.remainingMs);
            alarm.start();
            return;
        }

        finishIntervalCycle();
    }

    /**
     * Interval 结束处理
     */
    function finishIntervalCycle() {
        intervalState.isComplete = true;
        countdown.pause();
        updateButtonState('paused');
        alarm.stop();
        alarm.start();
    }

    /**
     * 处理 Meditation 间隔提示
     */
    function handleMeditationTick(remainingSeconds) {
        if (meditationState.intervalSeconds <= 0) {
            return;
        }

        if (remainingSeconds <= 0) {
            return;
        }

        if (meditationState.nextBellSeconds === null) {
            return;
        }

        if (remainingSeconds <= meditationState.nextBellSeconds) {
            alarm.playShortBeep();
            meditationState.nextBellSeconds -= meditationState.intervalSeconds;
            if (meditationState.nextBellSeconds <= 0) {
                meditationState.nextBellSeconds = null;
            }
        }
    }

    /**
     * 处理 Meditation 结束
     */
    function handleMeditationComplete() {
        meditationState.isComplete = true;
        countdown.pause();
        updateButtonState('paused');
        alarm.stop();
        alarm.start();
    }

    /**
     * 时间结构标准化
     */
    function normalizeTime(time) {
        return {
            hours: Math.max(0, parseInt(time?.hours || 0, 10)),
            minutes: Math.max(0, parseInt(time?.minutes || 0, 10)),
            seconds: Math.max(0, parseInt(time?.seconds || 0, 10))
        };
    }

    /**
     * 转为秒
     */
    function toSeconds(time) {
        return time.hours * 3600 + time.minutes * 60 + time.seconds;
    }

    /**
     * 处理 Start/Pause 按钮
     */
    function handleStartPause(action) {
        if (action === 'start') {
            if (currentMode === 'countdown') {
                if (currentCountdownType === 'interval' && intervalState.isComplete) {
                    resetIntervalTimer();
                }
                if (currentCountdownType === 'meditation' && meditationState.isComplete) {
                    resetMeditationTimer();
                }
            }
            currentTimer.start();
            // 同步 Worker：后台精确到期检测
            if (currentMode === 'countdown') {
                startWorkerTimer(countdown.remainingMs);
                // Meditation 间隔提示音也交给 Worker 调度
                if (currentCountdownType === 'meditation') {
                    scheduleMeditationBells(countdown.remainingMs);
                }
            }
            updateButtonState('running');
        } else if (action === 'pause') {
            currentTimer.pause();
            cancelWorkerTimer();
            updateButtonState('paused');
        }
    }

    /**
     * 处理 Reset/Split 按钮
     */
    function handleResetSplit(action) {
        if (action === 'reset') {
            if (currentMode === 'countdown') {
                if (currentCountdownType === 'interval') {
                    resetIntervalTimer();
                } else if (currentCountdownType === 'meditation') {
                    resetMeditationTimer();
                } else {
                    countdown.reset();
                    display.updateTime(countdown.getFormattedTime());
                }
            } else {
                currentTimer.reset();
                display.clearSplitRecords();
            }

            cancelWorkerTimer();
            alarm.stop();
            controls.setStopAlarmVisible(false);
            updateButtonState('idle');
        } else if (action === 'split') {
            // 只有 Stopwatch 有 split 功能
            if (currentMode === 'stopwatch') {
                stopwatch.split();
            }
        }
    }

    /**
     * 处理模式切换
     */
    function handleModeChange(mode) {
        // 停止当前计时器
        currentTimer.pause();
        cancelWorkerTimer();
        alarm.stop();
        controls.setStopAlarmVisible(false);

        currentMode = mode;

        if (mode === 'countdown') {
            currentTimer = countdown;
            display.toggleSplitRecords(false);
            applyCountdownType(currentCountdownType, true);
        } else {
            currentTimer = stopwatch;
            stopwatch.setShowMilliseconds(settings.getShowMilliseconds());
            stopwatch.reset();
            display.updateTime(stopwatch.getFormattedTime());
            display.toggleSplitRecords(false);
            display.hideIntervalStatus();
            controls.updateButtonsForStopwatch('idle');
        }
    }

    /**
     * 更新按钮状态
     */
    function updateButtonState(state) {
        if (currentMode === 'stopwatch') {
            controls.updateButtonsForStopwatch(state);

            // Stopwatch 运行时显示 Split 记录区域
            if (state === 'running' || state === 'paused') {
                display.toggleSplitRecords(true);
            }
        } else {
            controls.updateButtonsForCountdown(state);
        }
    }

    // 页面卸载时清理
    window.addEventListener('beforeunload', () => {
        countdown.destroy();
        stopwatch.destroy();
        controls.destroy();
        alarm.destroy();
        if (timerWorker) {
            timerWorker.terminate();
            timerWorker = null;
        }
    });
});
